/**
 * Manual YAML parser for authored field files (field-yaml-format.md).
 * No external dependencies — field YAML is simple enough for a hand-rolled parser.
 */

import type { FieldConfig, Cell, GemType } from "../../domain/types.js";
import type { AuthoredField, CellRole } from "../../domain/field-yaml.js";
import { GLYPH_TO_ROLE } from "../../domain/field-yaml.js";
import { bfsReachable } from "../../domain/pathfinding.js";
import { monstersPerWaveForWaves, spawnIntervalMsForWaves } from "./field-tuning.js";

// ---------------------------------------------------------------------------
// parseAuthoredField
// ---------------------------------------------------------------------------

/**
 * Parse an authored YAML field file into AuthoredField.
 * @param yamlText  Raw file contents.
 * @param fileName  Bare filename including extension, e.g. "G1.yml".
 *                  Used for error messages and id validation.
 */
export function parseAuthoredField(yamlText: string, fileName: string): AuthoredField {
  const expectedId = fileName.replace(/\.yml$/i, "");

  // Split into lines, preserve original (for line-number error messages).
  const lines = yamlText.split(/\r?\n/);

  // -------------------------------------------------------------------------
  // Locate the "grid: |" line — everything after it is the grid block.
  // -------------------------------------------------------------------------
  let gridStartLine = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^grid:\s*\|/.test(lines[i])) {
      gridStartLine = i;
      break;
    }
  }

  if (gridStartLine === -1) {
    throw new Error(`${fileName}: missing "grid: |" block`);
  }

  const headerLines = lines.slice(0, gridStartLine);
  const gridLines = lines.slice(gridStartLine + 1);

  // -------------------------------------------------------------------------
  // Parse header (simple "key: value" pairs)
  // -------------------------------------------------------------------------
  const header: Record<string, string> = {};
  for (const raw of headerLines) {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const colonIdx = trimmed.indexOf(":");
    if (colonIdx === -1) continue;
    const key = trimmed.slice(0, colonIdx).trim();
    // Strip inline comments from value.
    const rawValue = trimmed.slice(colonIdx + 1).trim().replace(/#.*$/, "").trim();
    if (key && rawValue !== "") {
      header[key] = rawValue;
    }
  }

  // Validate required keys. `id` is derived from the filename — the file is
  // its own identity, so there's no `id:` key to keep in sync.
  const required = [
    "wavesNum",
    "monsterBaseHp",
    "armorGrowthRate",
    "firstWaveDelayMs",
    "interWaveDelayMs",
  ] as const;
  for (const k of required) {
    if (!(k in header)) {
      throw new Error(`${fileName}: missing required header key "${k}"`);
    }
  }

  const id = expectedId;

  const wavesNum = Number(header["wavesNum"]);
  const monsterBaseHp = Number(header["monsterBaseHp"]);
  const armorGrowthRate = Number(header["armorGrowthRate"]);
  const firstWaveDelayMs = Number(header["firstWaveDelayMs"]);
  const interWaveDelayMs = Number(header["interWaveDelayMs"]);
  const initialMana =
    "initialMana" in header ? Number(header["initialMana"]) : undefined;
  const monstersPerWave =
    "monstersPerWave" in header ? Number(header["monstersPerWave"]) : undefined;
  const spawnIntervalMs =
    "spawnIntervalMs" in header ? Number(header["spawnIntervalMs"]) : undefined;

  // Parse allowedGemTypes: [Green, Orange] → ["Green", "Orange"]
  const VALID_GEM_TYPES = new Set<string>(
    ["Yellow", "Orange", "Red", "Purple", "Green", "Blue", "Cyan", "Lime"]
  );
  let allowedGemTypes: GemType[] | undefined;
  if ("allowedGemTypes" in header) {
    const raw = header["allowedGemTypes"].trim();
    // Expect format: [Type1, Type2, ...]
    const inner = raw.replace(/^\[/, "").replace(/\]$/, "");
    const parsed = inner
      .split(",")
      .map((s) => s.trim())
      .filter((s) => VALID_GEM_TYPES.has(s));
    if (parsed.length > 0) {
      allowedGemTypes = parsed as GemType[];
    }
  }

  // -------------------------------------------------------------------------
  // Parse grid block
  // -------------------------------------------------------------------------

  // Strip common leading whitespace (YAML block-scalar indentation).
  const rawGridRows: string[][] = [];
  for (const line of gridLines) {
    // Skip empty lines at end of grid (trailing blank lines in file).
    if (line.trim() === "") continue;
    // Split by whitespace to get individual glyphs.
    const glyphs = line.trim().split(/\s+/);
    rawGridRows.push(glyphs);
  }

  if (rawGridRows.length === 0) {
    throw new Error(`${fileName}: grid block is empty`);
  }

  const cols = rawGridRows[0].length;
  const rows = rawGridRows.length;

  // All rows must have the same number of glyphs.
  for (let r = 0; r < rawGridRows.length; r++) {
    if (rawGridRows[r].length !== cols) {
      throw new Error(
        `${fileName}:${gridStartLine + 1 + r + 1}: row ${r} has ${rawGridRows[r].length} glyphs but expected ${cols}`
      );
    }
  }

  // Convert glyphs to CellRole[], validate each glyph.
  const cells: CellRole[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const glyph = rawGridRows[r][c];
      if (!(glyph in GLYPH_TO_ROLE)) {
        throw new Error(
          `${fileName}:${gridStartLine + 1 + r + 1}:${c + 1}: unknown glyph '${glyph}'`
        );
      }
      cells.push(GLYPH_TO_ROLE[glyph]);
    }
  }

  // -------------------------------------------------------------------------
  // Extract cell lists from grid
  // -------------------------------------------------------------------------
  const entryCells: Cell[] = [];
  let exitCell: Cell | null = null;
  const walkableCells: Cell[] = [];
  const blockedCells: Cell[] = [];
  const prebuiltBuildings: Array<{ cell: Cell; type: import("../../domain/types.js").BuildingType }> = [];

  const BLOCKED_ROLES = new Set<CellRole>(["blockedA", "blockedB"]);

  const PREBUILT_TYPE_MAP: Partial<
    Record<CellRole, import("../../domain/types.js").BuildingType>
  > = {
    prebuiltChapel: "Tower",
    prebuiltWall: "Wall",
    prebuiltTrap: "Trap",
    prebuiltAmp: "Amplifier",
    prebuiltShrine: "Shrine",
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const role = cells[r * cols + c];
      const cell: Cell = { row: r, col: c };

      if (role === "entry") {
        entryCells.push(cell);
        walkableCells.push(cell);
      } else if (role === "exit") {
        if (exitCell !== null) {
          throw new Error(`${fileName}: more than one exit (">") in grid`);
        }
        exitCell = cell;
        walkableCells.push(cell);
      } else if (role === "path") {
        walkableCells.push(cell);
      } else if (BLOCKED_ROLES.has(role)) {
        blockedCells.push(cell);
      } else if (role in PREBUILT_TYPE_MAP) {
        prebuiltBuildings.push({ cell, type: PREBUILT_TYPE_MAP[role]! });
      }
      // "buildable" — neither walkable, blocked, nor prebuilt; just an open slot.
    }
  }

  // -------------------------------------------------------------------------
  // Validate topology
  // -------------------------------------------------------------------------
  if (exitCell === null) {
    throw new Error(`${fileName}: no exit (">") in grid`);
  }
  if (entryCells.length === 0) {
    throw new Error(`${fileName}: no entry ("<") in grid`);
  }

  // Build walkable lookup for BFS reachability check.
  const walkableSet = new Set<string>(walkableCells.map((c) => `${c.row},${c.col}`));
  const exit = exitCell; // narrowed

  for (const entry of entryCells) {
    const reachable = bfsReachable(
      rows,
      cols,
      entry,
      exit,
      (r, c) => walkableSet.has(`${r},${c}`)
    );
    if (!reachable) {
      throw new Error(
        `${fileName}: entry (${entry.row},${entry.col}) cannot reach exit`
      );
    }
  }

  return {
    id,
    wavesNum,
    monsterBaseHp,
    armorGrowthRate,
    firstWaveDelayMs,
    interWaveDelayMs,
    initialMana,
    monstersPerWave,
    spawnIntervalMs,
    grid: { rows, cols, cells },
    entryCells,
    exitCell: exit,
    walkableCells,
    blockedCells,
    prebuiltBuildings,
    allowedGemTypes,
  };
}

// ---------------------------------------------------------------------------
// authoredFieldToConfig
// ---------------------------------------------------------------------------

/**
 * Convert an AuthoredField into a FieldConfig usable by the game engine.
 * Phase D: no longer computes waypoints — uses entryCells/exitCell/walkableCells directly.
 */
export function authoredFieldToConfig(field: AuthoredField): FieldConfig {
  return {
    rows: field.grid.rows,
    cols: field.grid.cols,
    entryCells: field.entryCells,
    exitCell: field.exitCell,
    walkableCells: field.walkableCells,
    blockedCells: field.blockedCells,
    prebuiltBuildings: field.prebuiltBuildings.length > 0 ? field.prebuiltBuildings : undefined,
    wavesNum: field.wavesNum,
    monstersPerWave: field.monstersPerWave ?? monstersPerWaveForWaves(field.wavesNum),
    spawnIntervalMs: field.spawnIntervalMs ?? spawnIntervalMsForWaves(field.wavesNum),
    monsterBaseHp: field.monsterBaseHp,
    armorGrowthRate: field.armorGrowthRate,
    firstWaveDelayMs: field.firstWaveDelayMs,
    interWaveDelayMs: field.interWaveDelayMs,
    initialMana: field.initialMana,
    allowedGemTypes: field.allowedGemTypes,
  };
}
